import { Injectable } from '@angular/core';
import { DEFAULT_SEARCH_BAR_SETTINGS, SearchBarSettings } from '@zaoblako/web/core/models/search-bar-settings.model';
import type { ReportTypeEnum } from '@zaoblako/shared/modules/personalization/enums/report.type.enum';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';

export interface SavedFilterBarContext {
  typeFilter: ReportTypeEnum;
  queryParams: Record<string, unknown>;
  hasWriteAccessRight: boolean;
  hasReadAccessRight: boolean;
  extras?: unknown;
}

export interface SavedFilterQueryParamsChangePayload {
  extras?: unknown;
  [key: string]: unknown;
}

@Injectable()
export class ListSearchService {
  /** Актуальный текст из action-bar (без debounce), для синхронизации с queryParams при load(). */
  private currentSearchTerm = '';
  public currentEntitiesCounter = new BehaviorSubject<number>(null);
  public totalEntitiesCounter = new BehaviorSubject<number>(null);

  public loading = new BehaviorSubject<boolean>(false);
  public uploaderLoading = new BehaviorSubject<boolean>(false);

  public search = new Subject<string>();
  public searchQuery$: Observable<string>;

  public actionsSettings = new Subject<SearchBarSettings>();
  public savedFilterBarContext = new BehaviorSubject<SavedFilterBarContext | null>(null);
  public savedFilterQueryParamsChange = new Subject<SavedFilterQueryParamsChangePayload>();
  public mainButtonClick = new Subject<void>();
  public mainButtonSecondClick = new Subject<void>();
  public mainButtonThirdClick = new Subject<MouseEvent | undefined>();
  public importButtonClick = new Subject<void>();
  public exportButtonClick = new Subject<void>();
  /** Печать / PDF (например `window.print()` со страницы реестра) */
  public printPdfButtonClick = new Subject<void>();
  public settingsClick = new Subject<void>();
  public uploadHandler = new Subject<any>();

  public constructor() {
    this.search.pipe(tap((raw) => this.setCurrentSearchTerm(raw))).subscribe();

    this.searchQuery$ = this.search.pipe(
      map((query: string) => this.normalizeSearchTerm(query)),
      debounceTime(300),
      distinctUntilChanged()
    );
  }

  public getCurrentSearchTerm(): string {
    return this.currentSearchTerm;
  }

  private normalizeSearchTerm(raw: string | null | undefined): string {
    return (raw ?? '').trim();
  }

  private setCurrentSearchTerm(raw: string | null | undefined): void {
    this.currentSearchTerm = this.normalizeSearchTerm(raw);
  }

  public setLoadedCounters(totalEntitiesCounter: number, currentEntitiesCount?: number): void {
    this.totalEntitiesCounter.next(totalEntitiesCounter);
    this.currentEntitiesCounter.next(currentEntitiesCount ?? totalEntitiesCounter);
    this.loading.next(false);
  }

  public clearActionsSettings() {
    this.actionsSettings.next({ ...DEFAULT_SEARCH_BAR_SETTINGS });
    this.savedFilterBarContext.next(null);
  }
}
